<template>
	<div>
		<div class="tw-text-2xl tw-font-bold">
			{{ $t('integrations.noon-api-connection-title') }}
		</div>
		<div class="tw-font-medium tw-text-gray-500">
			{{ $t('integrations.noon-api-connection-description') }}
		</div>
		<a
			href="https://noon-docs.noonpartners.dev/docs/authentication/getting-credentials"
			target="_blank"
			class="tw-mt-1 tw-inline-block tw-text-sm tw-text-blue-500 tw-underline hover:tw-underline"
		>
			{{ $t('integrations.view-noon-documentation') }}
		</a>

		<!-- Connect method: OAuth (recommended, no key file) vs. a service-account key.
		     A segmented control so the active choice is unmistakable (filled = selected). -->
		<div
			v-if="!isDone"
			class="tw-mt-4 tw-inline-flex tw-gap-1 tw-rounded-full tw-border tw-border-gray-200 tw-bg-gray-100 tw-p-1"
		>
			<button
				type="button"
				:disabled="loading || oauthLoading"
				:class="[
					'tw-rounded-full tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-transition-colors disabled:tw-cursor-not-allowed',
					mode === 'oauth'
						? 'tw-bg-secondary-500 tw-text-white tw-shadow-sm'
						: 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700',
				]"
				@click="mode = 'oauth'"
			>
				{{ $t('integrations.noon-oauth-mode') }}
			</button>
			<button
				type="button"
				:disabled="loading || oauthLoading"
				:class="[
					'tw-rounded-full tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-transition-colors disabled:tw-cursor-not-allowed',
					mode === 'key'
						? 'tw-bg-secondary-500 tw-text-white tw-shadow-sm'
						: 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700',
				]"
				@click="mode = 'key'"
			>
				{{ $t('integrations.noon-key-mode') }}
			</button>
		</div>

		<AppForm
			@submit="onSubmit"
			class="tw-mt-4 tw-flex tw-flex-col tw-gap-3"
			v-if="!isDone"
			style="width: 90%"
		>
			<div
				v-if="mode === 'oauth'"
				class="tw-rounded-lg tw-border tw-border-secondary-200 tw-bg-secondary-50 tw-p-3 tw-text-sm tw-text-gray-600"
			>
				{{ $t('integrations.noon-oauth-description') }}
			</div>

			<AppInput
				v-model="form.store_name"
				:placeholder="$t('integrations.enter-store-name')"
				:label="$t('ziebart.store-name')"
				:rules="['required']"
				:disabled="loading || oauthLoading"
			/>

			<!-- Key-file mode only: upload + service account + private key + project code.
			     In OAuth mode noon returns these on the consent callback, so they're hidden. -->
			<template v-if="mode === 'key'">
				<!-- Upload noon_credentials_sensitive.json to auto-fill the key fields. -->
				<div
					class="tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-p-4"
				>
					<div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
						<div>
							<div class="tw-font-medium tw-text-gray-700">
								{{ $t('integrations.noon-upload-credentials-title') }}
							</div>
							<div class="tw-text-sm tw-text-gray-500">
								{{ $t('integrations.noon-upload-credentials-hint') }}
							</div>
						</div>
						<input
							ref="fileInputRef"
							type="file"
							accept=".json,application/json"
							class="tw-hidden"
							@change="onFileUpload"
						/>
						<AppButton
							:label="$t('integrations.noon-upload-credentials-button')"
							variant="gray"
							secondary
							type="button"
							startIcon="assets/v2/image/icons/upload.svg"
							:disabled="loading"
							@click="triggerFileUpload"
						/>
					</div>
					<div v-if="uploadedFileName" class="tw-mt-2 tw-text-sm tw-text-secondary-600">
						✓ {{ uploadedFileName }}
					</div>
					<div v-if="uploadError" class="tw-mt-2 tw-text-sm tw-text-error-500">
						{{ $t(uploadError, uploadErrorParams) }}
					</div>
				</div>

				<AppInput
					v-model="form.service_account_id"
					placeholder="svc-account-id"
					:label="$t('integrations.noon-service-account-id')"
					:rules="['required']"
					:disabled="loading"
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openHelpDialog('service-account')"
						>
							{{ $t('integrations.how-to-find-noon-service-account') }}
						</div>
					</template>
				</AppInput>

				<AppTextArea
					v-model="form.private_key"
					placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
					:label="$t('integrations.noon-private-key')"
					:rules="['required']"
					:rows="6"
					:disabled="loading"
				/>

				<AppInput
					v-model="form.project_code"
					:placeholder="$t('integrations.enter-noon-project-id')"
					:label="$t('integrations.noon-project-code')"
					:disabled="loading"
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openHelpDialog('project-id')"
						>
							{{ $t('integrations.how-to-find-noon-project-id') }}
						</div>
					</template>
				</AppInput>
			</template>

			<AppInput
				v-model="form.warehouse_code"
				:placeholder="$t('integrations.enter-noon-warehouse-code')"
				:label="$t('integrations.warehouse-code')"
				:rules="['required']"
				:disabled="loading || oauthLoading"
			>
				<template #hint>
					<div
						class="tw-cursor-pointer tw-text-gray-600 tw-underline"
						@click="openHelpDialog('warehouse-code')"
					>
						{{ $t('integrations.how-to-find-noon-warehouse-code') }}
					</div>
				</template>
			</AppInput>

			<div class="tw-grid tw-grid-cols-2 tw-gap-4">
				<AppSelect
					v-model="form.country_code"
					:label="$t('integrations.noon-country-code')"
					:options="countryOptions"
					optionLabel="label"
					optionValue="value"
					:disabled="loading || oauthLoading"
				/>
				<AppSelect
					v-model="form.processing_time"
					:label="$t('integrations.noon-processing-time')"
					:options="processingTimeOptions"
					optionLabel="label"
					optionValue="value"
					:disabled="loading || oauthLoading"
				/>
			</div>

			<div class="tw-flex tw-flex-col tw-gap-1">
				<AppSelect
					v-model="form.import_method"
					:label="$t('integrations.noon-import-method')"
					:options="importMethodOptions"
					optionLabel="label"
					optionValue="value"
					:disabled="loading || oauthLoading"
				/>
				<div class="tw-text-sm tw-text-gray-500">
					{{ $t('integrations.noon-import-method-note') }}
				</div>
			</div>

			<div v-if="form.import_method !== 'none'" class="tw-flex tw-flex-col tw-gap-1">
				<AppSelect
					v-model="form.warehouse_stock_mode"
					:label="$t('integrations.noon-warehouse-stock-mode')"
					:options="warehouseStockModeOptions"
					optionLabel="label"
					optionValue="value"
					:disabled="loading || oauthLoading"
				/>
				<div class="tw-text-sm tw-text-gray-500">
					{{ $t('integrations.noon-warehouse-stock-mode-note') }}
				</div>
			</div>

			<div v-if="oauthError" class="tw-text-sm tw-text-error-500">
				{{ $t(oauthError) }}
			</div>

			<div class="tw-mt-4">
				<AppButton
					v-if="mode === 'oauth'"
					:label="$t('integrations.noon-oauth-connect')"
					size="xl"
					class="tw-rounded-full !tw-px-6"
					type="button"
					:disabled="oauthLoading"
					@click="connectViaOAuth"
				/>
				<AppButton
					v-else
					:label="$t('integrations.connect')"
					size="xl"
					class="tw-rounded-full !tw-px-6"
					type="submit"
					:disabled="loading"
				/>
			</div>
		</AppForm>

		<div class="tw-mt-5" v-else>
			<div class="tw-text-xl tw-font-bold">{{ $t('integrations.your-store-connected') }}</div>

			<!-- noon cannot be told our callback URL over the API — the seller must paste it
			     into the warehouse configuration in Seller Lab. Show both values to copy. -->
			<div
				v-if="webhookDetails"
				class="tw-mt-4 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4"
				style="max-width: 90%"
			>
				<div class="tw-font-semibold tw-text-gray-700">
					{{ $t('integrations.noon-webhook-setup-title') }}
				</div>
				<div class="tw-mt-1 tw-text-sm tw-text-gray-500">
					{{ $t('integrations.noon-webhook-setup-description') }}
				</div>

				<div class="tw-mt-3 tw-flex tw-flex-col tw-gap-3">
					<div>
						<div class="tw-mb-1 tw-text-sm tw-font-medium tw-text-gray-700">
							{{ $t('integrations.noon-webhook-url') }}
						</div>
						<div class="tw-flex tw-items-center tw-gap-2">
							<code
								class="tw-flex-1 tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm"
								>{{ webhookDetails.url }}</code
							>
							<AppButton
								:label="
									copied === 'url' ? $t('integrations.copied') : $t('integrations.copy')
								"
								variant="gray"
								secondary
								type="button"
								@click="copy(webhookDetails.url, 'url')"
							/>
						</div>
					</div>

					<div>
						<div class="tw-mb-1 tw-text-sm tw-font-medium tw-text-gray-700">
							{{ $t('integrations.noon-webhook-api-key') }}
						</div>
						<div class="tw-flex tw-items-center tw-gap-2">
							<code
								class="tw-flex-1 tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm"
								>{{ webhookDetails.api_key }}</code
							>
							<AppButton
								:label="
									copied === 'key' ? $t('integrations.copied') : $t('integrations.copy')
								"
								variant="gray"
								secondary
								type="button"
								@click="copy(webhookDetails.api_key, 'key')"
							/>
						</div>
					</div>
				</div>

				<div class="tw-mt-3 tw-text-sm tw-text-gray-600">
					{{ $t('integrations.noon-webhook-setup-steps') }}
				</div>
				<a
					href="https://noon-docs.noonpartners.dev/docs/fbpi/setup/warehouse-setup"
					target="_blank"
					class="tw-mt-1 tw-inline-block tw-text-sm tw-text-blue-500 tw-underline hover:tw-underline"
				>
					{{ $t('integrations.view-noon-documentation') }}
				</a>
			</div>

			<div class="tw-mt-4">
				<AppButton
					:label="$t('integrations.next-step')"
					size="xl"
					class="tw-rounded-full"
					type="button"
					endIcon="assets/v2/image/icons/arrow-narrow-right.svg"
					@click="nextStep"
					:disabled="nextStepLoading"
				/>
			</div>
		</div>

		<HelpDialogServiceAccount ref="helpDialogServiceAccountRef" />
		<HelpDialogProjectId ref="helpDialogProjectIdRef" />
		<HelpDialogWarehouseCode ref="helpDialogWarehouseCodeRef" />
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useLinkMarketplaceStore } from '../../../../stores/link-marketplace';
import { Marketplace } from '../../../../entities/marketplace';
import { marketplaceStepStatusEnum } from '../../helper';
import HelpDialogServiceAccount from './help-dialog-service-account.vue';
import HelpDialogProjectId from './help-dialog-project-id.vue';
import HelpDialogWarehouseCode from './help-dialog-warehouse-code.vue';

const linkMarketplaceStore = useLinkMarketplaceStore();
const router = useRouter();
const { t } = useI18n();

// Connect method: 'oauth' (recommended, authorize on noon — no key file) or 'key'
// (paste/upload a service-account key). OAuth is the default.
const mode = ref('oauth');

const form = ref({
	store_name: '',
	service_account_id: '',
	private_key: '',
	project_code: '',
	warehouse_code: '',
	country_code: 'sa',
	processing_time: '1-2',
	// How to bring products in after connecting: 'api' (pull from noon — recommended,
	// runs in the background, may take a while), 'file' (upload an Excel file), or
	// 'none' (import later from the marketplace menu).
	import_method: 'api',
	// What to do with an imported product that holds no stock in the noon warehouse:
	// 'push' adds it there (price + quantity), 'pull_only' sends nothing to noon.
	warehouse_stock_mode: 'push',
});

// Import-method choices shown on connect. API is the recommended default. In OAuth mode
// the 'file' option is hidden: the smooth redirect to the file-upload page only happens in
// the manual submit path, so offering it there would dead-end after the noon redirect.
const allImportMethods = [
	{ label: t('integrations.noon-import-method-api'), value: 'api' },
	{ label: t('integrations.noon-import-method-file'), value: 'file' },
	{ label: t('integrations.noon-import-method-none'), value: 'none' },
];
// A seller who runs their noon warehouse from another system wants the catalog pulled in
// without Linker writing quantities to it. Adding them is the default: noon only routes
// orders for a SKU that holds stock in the warehouse.
const warehouseStockModeOptions = [
	{ label: t('integrations.noon-warehouse-stock-mode-push'), value: 'push' },
	{ label: t('integrations.noon-warehouse-stock-mode-pull-only'), value: 'pull_only' },
];

const importMethodOptions = computed(() =>
	mode.value === 'oauth' ? allImportMethods.filter((o) => o.value !== 'file') : allImportMethods,
);
// Switching to OAuth while 'file' is selected -> fall back to the recommended API import.
watch(mode, (m) => {
	if (m === 'oauth' && form.value.import_method === 'file') {
		form.value.import_method = 'api';
	}
});

// noon accepts only these country codes (lowercase).
const countryOptions = [
	{ label: 'Saudi Arabia (sa)', value: 'sa' },
	{ label: 'United Arab Emirates (ae)', value: 'ae' },
	{ label: 'Egypt (eg)', value: 'eg' },
];

const processingTimeOptions = [
	{ label: 'Same day', value: 'same_day' },
	{ label: '1–2 days', value: '1-2' },
	{ label: '2–3 days', value: '2-3' },
	{ label: '3–5 days', value: '3-5' },
];

const fileInputRef = ref(null);
const uploadedFileName = ref('');
const uploadError = ref('');
const uploadErrorParams = ref({});

const triggerFileUpload = () => fileInputRef.value?.click();

// Parse the noon credentials file (noon_credentials_sensitive.json /
// store_credentials.json) and auto-fill the service account fields. Keys per the
// noon docs: key_id, private_key, project_code.
const onFileUpload = (event) => {
	const file = event.target.files?.[0];
	event.target.value = ''; // allow re-selecting the same file
	if (!file) return;

	uploadError.value = '';
	uploadErrorParams.value = {};
	uploadedFileName.value = '';

	const reader = new FileReader();
	reader.onload = (e) => {
		try {
			const data = JSON.parse(e.target.result);
			const keyId = data.key_id ?? data.service_account_id;
			const privateKey = data.private_key;

			if (!keyId || !privateKey) {
				// A recognizable data-export file → name its type so the mistake is obvious.
				const exportType = data.exportType ?? data.export_type;
				if (exportType) {
					uploadError.value = 'integrations.noon-credentials-wrong-type';
					uploadErrorParams.value = { type: String(exportType) };
				} else {
					uploadError.value = 'integrations.noon-credentials-missing-fields';
				}
				return;
			}

			form.value.service_account_id = keyId;
			form.value.private_key = privateKey;
			if (data.project_code) form.value.project_code = data.project_code;

			uploadedFileName.value = file.name;
		} catch (err) {
			uploadError.value = 'integrations.noon-credentials-invalid';
		}
	};
	reader.onerror = () => {
		uploadError.value = 'integrations.noon-credentials-invalid';
	};
	reader.readAsText(file);
};

const helpDialogServiceAccountRef = ref(null);
const helpDialogProjectIdRef = ref(null);
const helpDialogWarehouseCodeRef = ref(null);

const openHelpDialog = (dialog) => {
	if (dialog === 'service-account') helpDialogServiceAccountRef.value.open();
	else if (dialog === 'project-id') helpDialogProjectIdRef.value.open();
	else helpDialogWarehouseCodeRef.value.open();
};

const loading = ref(false);
const isDone = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Complete,
);

// Callback URL + secret the seller must paste into their noon warehouse config.
const webhookDetails = ref(null);
const copied = ref('');

const loadWebhookDetails = async () => {
	webhookDetails.value = await Marketplace.getNoonWebhookDetails(
		linkMarketplaceStore.linkedMarketplaceId,
	);
};

const copy = async (text, which) => {
	try {
		await navigator.clipboard.writeText(text);
		copied.value = which;
		setTimeout(() => (copied.value = ''), 2000);
	} catch (err) {
		console.error(err);
	}
};

// Already connected (e.g. the seller reloaded the wizard) → show the values right away.
if (isDone.value) {
	loadWebhookDetails();
}

// OAuth connect: save the store details (no key), then send the browser to the noon
// consent screen. A full navigation (not XHR) so the web session carries the OAuth
// redirection route. noon returns the key on the callback and finalizes the connection.
const oauthLoading = ref(false);
const oauthError = ref('');
const connectViaOAuth = async () => {
	oauthError.value = '';
	if (!form.value.store_name || !form.value.warehouse_code) {
		oauthError.value = 'integrations.noon-oauth-missing-fields';
		return;
	}

	oauthLoading.value = true;
	try {
		const redirectUrl = await Marketplace.prepareNoonOAuth(
			{
				store_name: form.value.store_name,
				warehouse_code: form.value.warehouse_code,
				country_code: form.value.country_code,
				processing_time: form.value.processing_time,
				auto_integrate: form.value.import_method === 'api',
				list_imported_products: form.value.warehouse_stock_mode === 'push',
			},
			linkMarketplaceStore.linkedMarketplaceId,
		);

		if (redirectUrl) {
			window.location.href = redirectUrl;
		}
	} finally {
		oauthLoading.value = false;
	}
};

const onSubmit = async () => {
	loading.value = true;
	try {
		// Only the API method triggers the background pull-from-noon on connect.
		const success = await Marketplace.setNoonApiDetails(
			{
				...form.value,
				auto_integrate: form.value.import_method === 'api',
				list_imported_products: form.value.warehouse_stock_mode === 'push',
			},
			linkMarketplaceStore.linkedMarketplaceId,
		);
		if (success) {
			// File method: send the seller straight to the file-upload import page.
			if (form.value.import_method === 'file') {
				router.push({
					path: '/v2/imports/create/import-noon-product',
					query: { linked_marketplace_id: linkMarketplaceStore.linkedMarketplaceId },
				});
				return;
			}
			await linkMarketplaceStore.refetchSteps();
			isDone.value = true;
			// Surface the webhook URL + secret so the seller can finish setup in Seller Lab.
			await loadWebhookDetails();
		}
	} finally {
		loading.value = false;
	}
};

const nextStepLoading = ref(false);
const nextStep = async () => {
	nextStepLoading.value = true;
	try {
		await Marketplace.finishWizardStep(linkMarketplaceStore.currentStepData.id);
		await linkMarketplaceStore.refetchSteps();
	} finally {
		nextStepLoading.value = false;
	}
};
</script>

<style lang="scss" scoped></style>
